import React, { useState, useEffect } from "react";
import { useParams, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  User,
  ArrowLeft,
  CreditCard,
  BookOpen,
  Mail,
  Phone,
  GraduationCap,
  Loader,
} from "lucide-react";
import { lmsGetStudents } from "../api/lmsApi";
import "../lms.css";

const getSchoolData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const adminId = ud.school_id || ud.admin_id || ud.id || null;
    return { admin_id: adminId, ...ud };
  } catch {
    return { admin_id: null };
  }
};

export default function StudentHistoryLayout() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const school = getSchoolData();
  const schoolId = school.admin_id;

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (studentId) {
      fetchStudentInfo();
    }
  }, [studentId]);

  const fetchStudentInfo = async () => {
    setLoading(true);
    try {
      const res = await lmsGetStudents({ school_id: schoolId });
      const list = res.data?.data || (Array.isArray(res.data) ? res.data : []);
      const found = list.find(
        (s) => String(s.id) === String(studentId) || String(s.student_id) === String(studentId)
      );
      if (found) {
        setStudent(found);
      } else {
        setStudent({ id: studentId, first_name: "Student", last_name: `#${studentId}` });
      }
    } catch (err) {
      console.error("Error fetching student info:", err);
      setStudent({ id: studentId, first_name: "Student", last_name: `#${studentId}` });
    } finally {
      setLoading(false);
    }
  };

  const fName = student?.first_name || "";
  const lName = student?.last_name || "";
  const profileImg = typeof student?.profile_image === "string" ? student.profile_image : student?.profile_image_url;

  return (
    <div style={{ padding: "12px 16px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Top Header & Back Button */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
        <button
          type="button"
          onClick={() => navigate("/lms/students")}
          className="std-btn-cancel"
          style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "12px", padding: "4px 10px" }}
        >
          <ArrowLeft size={14} />
          Back to Students
        </button>

        <div style={{ fontSize: "11.5px", color: "#64748b", fontWeight: "500" }}>
          Student ID: <strong>#{studentId}</strong>
        </div>
      </div>

      {/* Student Profile Summary Header Card */}
      <div
        style={{
          background: "linear-gradient(135deg, #1c498d 0%, #2563eb 100%)",
          color: "#ffffff",
          borderRadius: "10px",
          padding: "12px 18px",
          marginBottom: "14px",
          boxShadow: "0 2px 8px rgba(28, 73, 141, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px" }}>
            <Loader className="spinner" size={18} color="#ffffff" />
            <span>Loading student profile...</span>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {profileImg ? (
                <img
                  src={profileImg}
                  alt="Student"
                  style={{ width: "38px", height: "38px", borderRadius: "50%", objectFit: "cover", border: "2px solid #ffffff" }}
                />
              ) : (
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.2)",
                    color: "#ffffff",
                    fontWeight: "700",
                    fontSize: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid rgba(255,255,255,0.4)",
                  }}
                >
                  {(fName[0] || "S").toUpperCase()}
                </div>
              )}
              <div>
                <h2 style={{ margin: 0, fontSize: "17px", fontWeight: "700" }}>
                  {fName} {lName}
                </h2>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "2px", fontSize: "12px", opacity: 0.9 }}>
                  <span>Adm No: <strong>{student?.admission_number || "N/A"}</strong></span>
                  <span>•</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <GraduationCap size={13} /> Class: <strong>{student?.class_name || student?.class_id || "N/A"}</strong>
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", fontSize: "12px", opacity: 0.95, background: "rgba(255,255,255,0.12)", padding: "6px 12px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.2)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <Mail size={13} />
                <span>{student?.email || "N/A"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <Phone size={13} />
                <span>{student?.mobile || "N/A"}</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Tabs Bar using NavLink */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          borderBottom: "2px solid #e2e8f0",
          marginBottom: "14px",
        }}
      >
        <NavLink
          to="payment-details"
          className={({ isActive }) =>
            `std-tab-btn ${isActive ? "active" : ""}`
          }
          style={({ isActive }) => ({
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 16px",
            fontWeight: "600",
            fontSize: "13px",
            textDecoration: "none",
            borderBottom: isActive ? "3px solid #1c498d" : "3px solid transparent",
            color: isActive ? "#1c498d" : "#64748b",
            background: isActive ? "#eff6ff" : "transparent",
            borderRadius: "6px 6px 0 0",
            transition: "all 0.2s ease",
          })}
        >
          <CreditCard size={15} />
          Student Payment Details
        </NavLink>

        <NavLink
          to="course-requests"
          className={({ isActive }) =>
            `std-tab-btn ${isActive ? "active" : ""}`
          }
          style={({ isActive }) => ({
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 16px",
            fontWeight: "600",
            fontSize: "13px",
            textDecoration: "none",
            borderBottom: isActive ? "3px solid #1c498d" : "3px solid transparent",
            color: isActive ? "#1c498d" : "#64748b",
            background: isActive ? "#eff6ff" : "transparent",
            borderRadius: "6px 6px 0 0",
            transition: "all 0.2s ease",
          })}
        >
          <BookOpen size={15} />
          Courses Request
        </NavLink>
      </div>

      {/* Render Tab Outlet */}
      <div>
        <Outlet context={{ student, schoolId }} />
      </div>
    </div>
  );
}
